import { useState, type ReactNode } from "react";
import moment from "moment/moment";
import {
  IconBan,
  IconChevronDown,
  IconDotsVertical,
  IconFileExport,
  IconFolderFilled,
  IconPlus,
} from "@tabler/icons-react";
import styles from "./DocumentListView.module.css";
import {
  DocumentListCell,
  DocumentListName,
  DocumentListRow,
  DocumentListSelectCell,
} from "./DocumentListRow";
import type { DiagnosticEntry } from "./MandatoryDiagnostics";
import { Table } from "../UI/Table";
import Text from "../UI/Text";
import Status from "../UI/Status";
import CountBadge from "../UI/CountBadge";
import DropdownMenu from "../UI/DropdownMenu";
import Tooltip from "../UI/Tooltip";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import {
  type DocumentFile,
  getGroupExpirationStatus,
} from "../../models/document";
import {
  type DocumentListEntry,
  type DocumentListSort,
  type DocumentListSortKey,
  latestFileDate,
  nextSort,
  sortEntries,
  sortFiles,
} from "../../utils/documentList";

export interface RenderFileOptions {
  /** Fichier d'un dossier déplié. */
  child: boolean;
  last: boolean;
}

interface DocumentListViewProps {
  label: string;
  entries: DocumentListEntry[];
  /** Diagnostics attendus sans aucun fichier, proposés en ajout rapide. */
  missing?: DiagnosticEntry[];
  renderFile: (file: DocumentFile, options: RenderFileOptions) => ReactNode;
  selectedIds: Set<number>;
  onSelectionChange: (fileIds: number[], selected: boolean) => void;
  isSelectable: (file: DocumentFile) => boolean;
  showShareColumn: boolean;
  onDownloadZip?: (files: DocumentFile[]) => void;
  onAddMissing?: (tag: string) => void;
  onSetNotApplicable?: (tag: string, notApplicable: boolean) => void;
  profileBailleurId?: number;
}

function formatDate(date?: string) {
  return date ? moment(date).format("DD/MM/YYYY") : "";
}

function sharedLabel(files: DocumentFile[]) {
  if (files.every((file) => file.shared)) return "Tous partagés";
  if (files.some((file) => file.shared)) return "En partie";
  return "Non";
}

/**
 * Vue liste des documents : une ligne par fichier, les dossiers se déplient en
 * accordéon, l'en-tête trie et sélectionne. Les lignes de fichier sont rendues
 * par l'appelant (`renderFile`), qui porte les actions du document.
 */
export default function DocumentListView({
  label,
  entries,
  missing = [],
  renderFile,
  selectedIds,
  onSelectionChange,
  isSelectable,
  showShareColumn,
  onDownloadZip,
  onAddMissing,
  onSetNotApplicable,
  profileBailleurId,
}: DocumentListViewProps) {
  const [sort, setSort] = useState<DocumentListSort | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const missingEntries: DocumentListEntry[] = missing.map((entry) => ({
    key: `missing-${entry.tag}`,
    kind: "missing",
    title: entry.label,
    files: [],
  }));
  const sorted = sortEntries([...entries, ...missingEntries], sort);
  const selectableFiles = entries
    .flatMap((entry) => entry.files)
    .filter(isSelectable);
  const selectedCount = selectableFiles.filter((file) =>
    selectedIds.has(file.id),
  ).length;

  function toggleFolder(key: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function sortDirection(key: DocumentListSortKey) {
    return sort?.key === key ? sort.direction : null;
  }

  function sortColumn(key: DocumentListSortKey, title: string) {
    return (
      <Table.SortButton
        direction={sortDirection(key)}
        onSort={() => setSort(nextSort(sort, key))}
      >
        {title}
      </Table.SortButton>
    );
  }

  function renderFolder(entry: DocumentListEntry) {
    const selectable = entry.files.filter(isSelectable);
    const selectedInFolder = selectable.filter((file) =>
      selectedIds.has(file.id),
    ).length;
    const allSelected =
      selectable.length > 0 && selectedInFolder === selectable.length;
    const isExpanded = expanded.has(entry.key);
    const childrenId = `document-folder-${entry.key}`;
    const groupExpiration = getGroupExpirationStatus(entry.files);
    const files = sortFiles(entry.files, sort);
    return (
      <div key={entry.key} role="rowgroup">
        <DocumentListRow selected={selectedInFolder > 0}>
          <DocumentListSelectCell
            checked={allSelected}
            indeterminate={selectedInFolder > 0}
            label={`Sélectionner tous les documents du dossier ${entry.title}`}
            onChange={
              selectable.length > 0
                ? (checked) => {
                    onSelectionChange(
                      selectable.map((file) => file.id),
                      checked,
                    );
                    if (checked && !isExpanded) toggleFolder(entry.key);
                  }
                : undefined
            }
          />
          <DocumentListName
            icon={IconFolderFilled}
            title={entry.title}
            mobileSubtitle={`${entry.files.length} documents`}
            onClick={() => toggleFolder(entry.key)}
            expanded={isExpanded}
            controls={childrenId}
            trailing={
              <>
                <CountBadge count={entry.files.length} />
                <IconChevronDown
                  size={18}
                  className={classnames(
                    styles.chevron,
                    isExpanded && styles.chevronOpen,
                  )}
                />
              </>
            }
          />
          <DocumentListCell column="status">
            {groupExpiration === "expired" && (
              <Status color="danger" text="Expiré" />
            )}
            {groupExpiration === "expiring" && (
              <Status color="warning" text="Expire bientôt" />
            )}
          </DocumentListCell>
          <DocumentListCell column="share">
            {showShareColumn && (
              <Text variant="deci">{sharedLabel(entry.files)}</Text>
            )}
          </DocumentListCell>
          <DocumentListCell column="date">
            <Text variant="deci">
              {formatDate(latestFileDate(entry.files))}
            </Text>
          </DocumentListCell>
          <DocumentListCell column="actions">
            {onDownloadZip && (
              <DropdownMenu
                icon={IconDotsVertical}
                label={`Actions sur le dossier ${entry.title}`}
                align="end"
              >
                <DropdownMenu.Item
                  icon={IconFileExport}
                  label="Tout télécharger (ZIP)"
                  onClick={() => onDownloadZip(entry.files)}
                />
              </DropdownMenu>
            )}
          </DocumentListCell>
        </DocumentListRow>
        <div id={childrenId} hidden={!isExpanded}>
          {files.map((file, index) =>
            renderFile(file, { child: true, last: index === files.length - 1 }),
          )}
        </div>
      </div>
    );
  }

  function renderMissing(entry: DocumentListEntry) {
    const diagnostic = missing.find(
      (candidate) => `missing-${candidate.tag}` === entry.key,
    )!;
    const name = (
      <DocumentListName
        icon={IconPlus}
        title={diagnostic.label}
        subtitle={diagnostic.reason ?? diagnostic.name}
        onClick={onAddMissing ? () => onAddMissing(diagnostic.tag) : undefined}
        muted
        className={styles.missingName}
      />
    );
    return (
      <DocumentListRow key={entry.key}>
        <DocumentListSelectCell
          checked={false}
          label="Aucun document à sélectionner"
        />
        {onAddMissing ? (
          <Tooltip content={`Ajouter : ${diagnostic.name}`}>{name}</Tooltip>
        ) : (
          name
        )}
        <DocumentListCell column="status">
          <Status
            color="neutral"
            text="À fournir"
            className={styles.missingStatus}
          />
        </DocumentListCell>
        <DocumentListCell column="share" />
        <DocumentListCell column="date" />
        <DocumentListCell column="actions">
          {onAddMissing && onSetNotApplicable && (
            <DropdownMenu
              icon={IconDotsVertical}
              label={`Actions : ${diagnostic.name}`}
              align="end"
            >
              <DropdownMenu.Item
                icon={IconPlus}
                label="Ajouter le document"
                requiresWrite
                profileId={profileBailleurId}
                onClick={() => onAddMissing(diagnostic.tag)}
              />
              <DropdownMenu.Item
                icon={IconBan}
                label="Ce bien n'est pas concerné"
                requiresWrite
                profileId={profileBailleurId}
                onClick={() => onSetNotApplicable(diagnostic.tag, true)}
              />
            </DropdownMenu>
          )}
        </DocumentListCell>
      </DocumentListRow>
    );
  }

  return (
    <div role="table" aria-label={label} className={styles.table}>
      <DocumentListRow header>
        <DocumentListSelectCell
          header
          checked={
            selectableFiles.length > 0 &&
            selectedCount === selectableFiles.length
          }
          indeterminate={selectedCount > 0}
          label="Tout sélectionner"
          onChange={
            selectableFiles.length > 0
              ? (checked) =>
                  onSelectionChange(
                    selectableFiles.map((file) => file.id),
                    checked,
                  )
              : undefined
          }
        />
        <div role="columnheader" className={styles.headerCell}>
          {sortColumn("title", "Document")}
        </div>
        <DocumentListCell header column="status">
          {sortColumn("status", "Statut")}
        </DocumentListCell>
        <DocumentListCell header column="share">
          {showShareColumn && "Partagé au locataire"}
        </DocumentListCell>
        <DocumentListCell header column="date">
          {sortColumn("date", "Ajouté le")}
        </DocumentListCell>
        <DocumentListCell header column="actions" />
      </DocumentListRow>
      {sorted.map((entry) => {
        if (entry.kind === "folder") return renderFolder(entry);
        if (entry.kind === "missing") return renderMissing(entry);
        return (
          <div key={entry.key} role="rowgroup">
            {renderFile(entry.files[0], { child: false, last: false })}
          </div>
        );
      })}
    </div>
  );
}
